import React, { useState, useEffect, useContext, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Book, ArrowLeft, Loader, User, AlertCircle, Download, 
  ThumbsUp, ThumbsDown, Upload, FileText, Flag, Bookmark,
  BookmarkCheck, Filter, ArrowUpDown, Calendar, Eye,
  MessageSquare, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import SectionQuestionsTab from '../../components/questions/SectionQuestionsTab';
import ComplianceMaterialUpload from '../../components/ComplianceMaterialUpload';
const DocumentViewer = lazy(() => import('../../components/DocumentViewer'));

/**
 * Course Section Page component
 * Shows section information and materials
 */
const CourseSectionPage = () => {
  const { courseId, sectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [section, setSection] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('materials');
  
  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState(null);

  // Material upload state
  const [uploading, setUploading] = useState(false);

  // Comment counts
  const [commentCounts, setCommentCounts] = useState({});

  useEffect(() => {
    const fetchSectionDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch course details
        const courseRes = await fetch(`${process.env.REACT_APP_API_URL}/api/courses/${courseId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!courseRes.ok) {
          throw new Error('Failed to fetch course details');
        }

        const courseData = await courseRes.json();
        setCourse(courseData);
        
        // Find the correct section
        const sectionData = courseData.sections?.find(s => s.id === sectionId);
        setSection(sectionData || null);
        
        if (!sectionData) {
          throw new Error('Section not found');
        }
        
        // Fetch materials for this section
        const materialsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/course-sections/${sectionId}/materials`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!materialsRes.ok) {
          throw new Error('Failed to fetch materials');
        }

        const materialsData = await materialsRes.json();
        setMaterials(materialsData || []);
        
        // Fetch comment counts for materials
        if (materialsData && materialsData.length > 0) {
          fetchCommentCounts(materialsData.map(m => m.id));
        }
        
        // Check if user is subscribed to this section
        const subscriptionsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${user.id}/subscriptions`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (subscriptionsRes.ok) {
          const subscriptionsData = await subscriptionsRes.json();
          const subscription = subscriptionsData.find(s => s.sectionId === sectionId);
          
          if (subscription) {
            setIsSubscribed(true);
            setSubscriptionId(subscription.id);
          }
        }
      } catch (error) {
        console.error('Error fetching section details:', error);
        setError(error.message || 'An error occurred while loading section data');
        toast.error('Failed to load section details');
      } finally {
        setLoading(false);
      }
    };

    if (courseId && sectionId) {
      fetchSectionDetails();
    }
  }, [courseId, sectionId, user.id]);

  // Fetch comment counts for materials using batch API
  const fetchCommentCounts = async (materialIds) => {
    if (!materialIds || materialIds.length === 0) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/comments/counts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ materialIds })
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch comment counts');
      }
      
      const counts = await response.json();
      setCommentCounts(counts);
    } catch (error) {
      console.error('Error fetching comment counts:', error);
    }
  };

  // Filter and sort materials
  const filteredMaterials = materials
    .filter(material => !typeFilter || material.type === typeFilter)
    .sort((a, b) => {
      if (sortOrder === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortOrder === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortOrder === 'most-upvoted') return b.upvotes - a.upvotes;
      if (sortOrder === 'most-downvoted') return b.downvotes - a.downvotes;
      return 0;
    });

  // Function to handle authenticated downloads
  const handleDownload = async (material) => {
    try {
      toast.loading('Preparing download...');
      
      // Process the URL to make sure it's properly formatted
      let apiUrl;
      const fileUrl = material.fileUrl;
      
      // If it's already an API URL, use it directly
      if (fileUrl.startsWith('/api/files/')) {
        apiUrl = `${process.env.REACT_APP_API_URL}${fileUrl}`;
      } 
      // Otherwise, construct the proper path
      else {
        // Extract the file path - this may need adjustment based on your URL structure
        let filePath;
        if (fileUrl.includes('api.edx.red')) {
          try {
            const urlObj = new URL(fileUrl);
            filePath = urlObj.pathname.replace(/^\/api\/files\//, '');
          } catch (e) {
            console.error("Error parsing URL:", e);
            filePath = fileUrl.split('/').pop();
          }
        } else {
          filePath = fileUrl;
        }
        
        apiUrl = `${process.env.REACT_APP_API_URL}/api/files/${filePath}`;
      }
      
      // Add download parameter
      apiUrl += '?download=true';
      
      // Fetch the file with authentication
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the file data as a blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const downloadUrl = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = material.title || material.fileUrl.split('/').pop();
      
      // Append to the document
      document.body.appendChild(a);
      
      // Click the link
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      
      toast.dismiss();
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.dismiss();
      toast.error(`Download failed: ${error.message}`);
    }
  };

  // Open the document viewer for a material
  const handleViewMaterial = (material) => {
    // Track the view
    trackMaterialView(material.id);
    
    // Set the current material and open the viewer
    setCurrentMaterial(material);
    setViewerOpen(true);
  };

  // Track material view
  const trackMaterialView = async (materialId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        console.log(`Successfully tracked view for material ${materialId}`);
      } else {
        console.error('Failed to track material view:', await response.text());
      }
    } catch (error) {
      console.error('Error tracking material view:', error);
    }
  };

  // Close the document viewer
  const handleCloseViewer = () => {
    setViewerOpen(false);
    setCurrentMaterial(null);
    
    // Refresh comment counts after viewing
    if (materials && materials.length > 0) {
      fetchCommentCounts(materials.map(m => m.id));
    }
  };

  // Check if a file type is supported by the viewer
  const isSupportedFileType = (fileUrl, fileType) => {
    // All file types are now supported in some way
    return true;
  };

  // Subscribe to course section
  const handleSubscribe = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/course-sections/${sectionId}/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }
      
      const data = await response.json();
      setIsSubscribed(true);
      setSubscriptionId(data.id);
      toast.success('Subscribed to course section!');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed to subscribe to course section');
    }
  };

  // Unsubscribe from course section
  const handleUnsubscribe = async () => {
    if (!subscriptionId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to unsubscribe');
      }
      
      setIsSubscribed(false);
      setSubscriptionId(null);
      toast.success('Unsubscribed from course section');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to unsubscribe from course section');
    }
  };

  // Vote on a material
  const handleVote = async (materialId, voteType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ voteType })
      });
      
      if (!response.ok) {
        throw new Error('Failed to vote');
      }
      
      const updatedVote = await response.json();
      
      // Update materials state with new vote counts
      setMaterials(materials.map(material => 
        material.id === materialId 
          ? { ...material, upvotes: updatedVote.upvotes, downvotes: updatedVote.downvotes } 
          : material
      ));
      
      toast.success(`${voteType === 'upvote' ? 'Upvoted' : 'Downvoted'} material`);
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote on material');
    }
  };

  // Report a material
  const handleReport = async (materialId) => {
    const reason = prompt('Please provide a reason for reporting this material:');
    if (!reason || !reason.trim()) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: reason.trim() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to report material');
      }
      
      toast.success('Material reported successfully');
    } catch (error) {
      console.error('Error reporting material:', error);
      toast.error(error.message || 'Failed to report material');
    }
  };

  // Delete material
  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material? This action cannot be undone.')) {
      return;
    }
    
    // Show loading state and optimistically remove from UI
    toast.loading('Deleting material...', { id: `delete-${materialId}` });
    const materialToDelete = materials.find(m => m.id === materialId);
    setMaterials(materials.filter(material => material.id !== materialId));
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        // If delete failed, restore the material to the list
        setMaterials(prevMaterials => [...prevMaterials, materialToDelete].sort((a, b) => b.createdAt - a.createdAt));
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete material');
      }
      
      toast.success('Material deleted successfully', { id: `delete-${materialId}` });
    } catch (error) {
      console.error('Error deleting material:', error);
      toast.error(error.message || 'Failed to delete material', { id: `delete-${materialId}` });
    }
  };

  // Upload material
  const handleUploadSubmit = async (materialData) => {
    if (!materialData.file || !materialData.title || !materialData.type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', materialData.file);
      formData.append('title', materialData.title);
      formData.append('description', materialData.description || '');
      formData.append('type', materialData.type);
      formData.append('courseId', courseId);
      formData.append('courseSectionId', sectionId);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle compliance validation errors specially
        if (response.status === 422) {
          toast.error(`Content Compliance Error: ${errorData.message}`);
          if (errorData.details && errorData.details.length > 0) {
            errorData.details.forEach(detail => {
              toast.error(detail.message || detail, { duration: 6000 });
            });
          }
        } else {
          throw new Error(errorData.error || 'Failed to upload material');
        }
        return;
      }
      
      const newMaterial = await response.json();
      
      // Add new material to state
      setMaterials([newMaterial, ...materials]);
      setUploadModalOpen(false);
      
      toast.success('Material uploaded successfully!');
      
      // Show additional message if under review
      if (newMaterial.complianceStatus === 'under_review') {
        toast.info('Your material is under review and will be published after approval.', { duration: 5000 });
      }
      
    } catch (error) {
      console.error('Error uploading material:', error);
      toast.error('Failed to upload material: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Loader className="h-12 w-12 text-red-500 animate-spin mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
          Loading course section...
        </h2>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Section
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Go Back
        </button>
      </div>
    );
  }

  // If section data is missing
  if (!course || !section) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Course Section Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The course section you're looking for couldn't be found or you don't have access to it.
        </p>
        <Link
          to={`/courses/${courseId}`}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Course
        </Link>
      </div>
    );
  }


  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation breadcrumbs */}
      <div className="flex items-center mb-6 text-sm">
        <Link to="/school" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Home
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link to="/courses" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Courses
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link to={`/courses/${course.id}`} className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          {course.code}
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {section.semester} {section.year}
        </span>
      </div>

      {/* Section header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {course.code}: {course.name}
            </h1>
            <div className="flex items-center text-gray-700 dark:text-gray-300 mb-4">
              <Calendar className="h-4 w-4 mr-1" />
              <span>{section.semester} {section.year}</span>
            </div>
            <div className="flex items-center">
              <User className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-lg text-gray-700 dark:text-gray-300">
                Professor: {section.professorName}
              </span>
            </div>
          </div>
          
          <div className="mt-4 md:mt-0 flex flex-col space-y-2">
            {isSubscribed ? (
              <button
                onClick={handleUnsubscribe}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <BookmarkCheck className="mr-2 -ml-1 h-5 w-5 text-red-500" />
                Subscribed
              </button>
            ) : (
              <button
                onClick={handleSubscribe}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Bookmark className="mr-2 -ml-1 h-5 w-5" />
                Subscribe
              </button>
            )}
            
            <button
              onClick={() => setUploadModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <Upload className="mr-2 -ml-1 h-5 w-5" />
              Upload Material
            </button>
          </div>
        </div>
      </div>

      {/* Materials and Questions Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('materials')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'materials'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <FileText className="inline-block mr-2 h-4 w-4" />
              Materials
            </button>
            <button
              onClick={() => setActiveTab('questions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'questions'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <MessageSquare className="inline-block mr-2 h-4 w-4" />
              Questions
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'materials' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Materials
                </h2>
          
          {/* Filters */}
          <div className="mt-4 md:mt-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <Filter className="h-4 w-4 mr-1" />
              <span className="text-sm">{showFilters ? 'Hide filters' : 'Show filters'}</span>
            </button>
          </div>
        </div>
        
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Material Type
                </label>
                <select
                  id="type-filter"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">All Types</option>
                  <option value="notes">Notes</option>
                  <option value="exam">Exams</option>
                  <option value="syllabus">Syllabi</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort By
                </label>
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="most-upvoted">Most Upvoted</option>
                  <option value="most-downvoted">Most Downvoted</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {filteredMaterials.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Materials Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {typeFilter 
                ? `No materials of type "${typeFilter}" found for this course section.` 
                : "This course section doesn't have any materials uploaded yet."
              }
            </p>
            <button
              onClick={() => setUploadModalOpen(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Upload className="mr-2 -ml-1 h-5 w-5" />
              Upload Material
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredMaterials.map((material) => (
              <div
                key={material.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-red-500 dark:hover:border-red-500 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start">
                  <div className="flex-1">
                    <div className="flex items-start">
                      <div
                        className={`p-2 rounded-md mr-3 ${
                          material.type === 'notes'
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
                            : material.type === 'exam'
                            ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300'
                            : material.type === 'syllabus'
                            ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <FileText size={20} />
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {material.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Uploaded by <Link 
                            to={`/users/${material.userId}`}
                            className="hover:text-red-600 dark:hover:text-red-400"
                          >
                            {material.userName}
                          </Link>
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {new Date(material.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    {material.description && (
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 ml-12">
                        {material.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-4 md:mt-0 md:ml-4 flex space-x-2">
                    {/* View button */}
                    <button
                      onClick={() => handleViewMaterial(material)}
                      className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Eye className="mr-1 -ml-1 h-4 w-4" />
                      View
                    </button>
                    
                    {/* Download button */}
                    <button
                      onClick={() => handleDownload(material)}
                      className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Download className="mr-1 -ml-1 h-4 w-4" />
                      Download
                    </button>
                  </div>
                </div>
                
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handleVote(material.id, 'upvote')}
                      className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                    >
                      <ThumbsUp className="h-4 w-4 mr-1" />
                      <span>{material.upvotes}</span>
                    </button>
                    
                    <button
                      onClick={() => handleVote(material.id, 'downvote')}
                      className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <ThumbsDown className="h-4 w-4 mr-1" />
                      <span>{material.downvotes}</span>
                    </button>
                    
                    <button
                      onClick={() => handleViewMaterial(material)}
                      className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      <span>{commentCounts[material.id] || 0}</span>
                    </button>
                    
                    <button
                      onClick={() => handleReport(material.id)}
                      className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400"
                    >
                      <Flag className="h-4 w-4 mr-1" />
                      <span className="text-sm">Report</span>
                    </button>
                    
                    {/* Delete button - only show for material owner or moderators/admins */}
                    {(user && (material.userName === user.name || user.role === 'moderator' || user.role === 'admin')) && (
                      <button
                        onClick={() => handleDeleteMaterial(material.id)}
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        <span className="text-sm">Delete</span>
                      </button>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    {material.fileType} • {Math.round(material.fileSize / 1024)} KB
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
            </div>
          )}

          {activeTab === 'questions' && (
            <SectionQuestionsTab
              courseId={courseId}
              sectionId={sectionId}
              courseName={course?.name}
              courseCode={course?.code}
              semester={section?.semester}
              year={section?.year}
              professorName={section?.professorName}
            />
          )}
        </div>
      </div>
      
      {/* Upload material modal */}
      <ComplianceMaterialUpload
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUploadSubmit}
        uploading={uploading}
      />
      
      {/* Document viewer modal */}
      {viewerOpen && currentMaterial && (
        <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-red-500 rounded-full animate-spin"></div>
        </div>}>
          <DocumentViewer
            fileUrl={currentMaterial.fileUrl}
            fileType={currentMaterial.fileType}
            title={currentMaterial.title}
            token={localStorage.getItem('token')}
            materialId={currentMaterial.id}
            currentUser={user}
            onClose={handleCloseViewer}
            sidebarWidth={256}
          />
        </Suspense>
      )}
    </div>
  );
};

export default CourseSectionPage;